import type {
	ChampionData,
	ChampionByPromoterData,
	CombatregRanking,
	UfcRanking,
	CombatregFighterRanking,
	UfcFighterRanking,
	BoutResponse,
	BoutStatsResponse,
	TotalsStats,
	Fighter,
	Round,
	StrikesStats,
	DetailedEventResponse
} from './types';

import type {
	PromotionInfo,
	ChampionInfo,
	PromotionRankings,
	FighterRanking,
	BoutInfo,
	EventInfo,
	BoutStats,
	BoutSummaryStats,
	BoutInDepthStats,
	AggregatedPunchStats,
	FighterAttributes,
	EventWithBouts,
	Sport
} from '../../types';
import { inchesToFeet, dateOfBirthToAge, ensure } from '../../utils';

const P4PLimit = 5;

export function mapPromotionWithChampions(data: ChampionByPromoterData): ChampionInfo[] {
	return data.champions.map(buildChampion);
}

const mapRecord = (
	fighter: CombatregFighterRanking
): {
	wins: number;
	losses: number;
	draws: number;
} => {
	if ('bkfc' in fighter.record) {
		return fighter.record.bkfc;
	} else {
		return fighter.record.pfl;
	}
};

const mapCombatregFighter = (fighter: CombatregFighterRanking): FighterRanking => {
	const record = mapRecord(fighter);
	return {
		id: fighter.uuid,
		firstName: fighter.first_name,
		lastName: fighter.last_name,
		displayName: `${fighter.first_name} ${fighter.last_name}`,
		rank: fighter.rank,
		previousRank: fighter.previous_rank,
		wins: record.wins,
		losses: record.losses,
		draws: record.draws,
		interim: fighter.interim_champion || false
	};
};

const mapUfcFighter = (fighter: UfcFighterRanking): FighterRanking => {
	return {
		id: fighter.FighterID.toString(),
		firstName: fighter.FirstName,
		lastName: fighter.LastName,
		displayName: `${fighter.FirstName} ${fighter.LastName}`,
		rank: fighter.Rank,
		previousRank: fighter.PreviousRank,
		wins: parseInt(fighter.Wins, 10),
		losses: parseInt(fighter.Losses, 10),
		draws: parseInt(fighter.Draws, 10),
		interim: fighter.Interim_Champion || false
	};
};

export function mapCombatregRankings(
	promotion: PromotionInfo,
	ranking: CombatregRanking
): PromotionRankings {
	ranking.wp4p ??= [];
	return {
		promotionName: promotion.name,
		maleRankings: ranking.p4p.slice(0, P4PLimit).map(mapCombatregFighter),
		femaleRankings: ranking.wp4p.slice(0, P4PLimit).map(mapCombatregFighter)
	};
}

export function mapUfcRankings(ranking: UfcRanking): PromotionRankings {
	return {
		promotionName: 'UFC',
		maleRankings: ranking["Men's P4P"].slice(0, P4PLimit).map(mapUfcFighter),
		femaleRankings: ranking["Women's P4P"].slice(0, P4PLimit).map(mapUfcFighter)
	};
}

function buildChampion(championData: ChampionData) {
	return {
		id: championData.fighter.uuid,
		weightDivision: championData.weight_division,
		displayName: `${championData.fighter.first_name} ${championData.fighter.last_name}`,
		interim: championData.interim,
		imageUrl: championData.fighter.public_image_url,
		height: inchesToFeet(championData.fighter.height),
		weight: championData.fighter.weight,
		age: dateOfBirthToAge(championData.fighter.date_of_birth),
		nationality: championData.fighter.nationality,
		wins: championData.fighter.wins,
		losses: championData.fighter.losses,
		draws: championData.fighter.draws,
		wins_by_ko: championData.fighter.wins_by_ko,
		encryptedId: championData.fighter.encrypted_id,
		spaceFreeName: `${championData.fighter.first_name}-${championData.fighter.last_name}`
	};
}

const mapFighter = (fighter: Fighter): FighterAttributes => {
	return {
		id: fighter.uuid,
		firstName: fighter.first_name,
		lastName: fighter.last_name,
		displayName: `${fighter.first_name} ${fighter.last_name}`,
		imageUrl: fighter.public_image_url,
		height: inchesToFeet(fighter.height),
		weight: fighter.weight,
		age: dateOfBirthToAge(fighter.date_of_birth),
		nationality: fighter.nationality || '',
		lastFightDate: fighter.last_fight_date,
		professionalRecords: {
			bkfc: fighter.pro_records['Bare Knuckle Fighting'],
			mma: fighter.pro_records.MMA
		}
	};
};

export function mapBout(bout: BoutResponse): BoutInfo {
	const event: EventInfo = {
		name: bout.fight.event_name,
		date: bout.fight.event_date ? new Date(bout.fight.event_date) : null,
		isExhibition: bout.fight.exhibition,
		venue: bout.fight.venue,
		organization: bout.fight.organization_name,
		promotion: bout.fight.promotion_name,
		promotion_logo: bout.fight.promotion_logo,
		sport: bout.fight.sport_name as Sport,
		city: bout.fight.event_city,
		state: bout.fight.event_state,
		country: bout.fight.event_country
	};

	const blueCornerFighter = ensure(bout.fighters.find((f) => f.corner === 'blue'));
	const redCornerFighter = ensure(bout.fighters.find((f) => f.corner === 'red'));

	return {
		event: event,
		weightclass: bout.status.weight_division,
		roundSchedule: bout.fight.round_schedule,
		winner: {
			id: bout.status.winner?.uuid,
			corner: bout.status.winner_corner
		},
		resultType: bout.status.result_type,
		resultDetails: bout.status.result_detail,
		attributes: {
			blueCorner: mapFighter(blueCornerFighter),
			redCorner: mapFighter(redCornerFighter)
		},
		statistic: {
			liveId: bout.fight.live_stat_id,
			finalId: bout.fight.final_stat_id
		}
	};
}

function extractSummaryStats(stats: StrikesStats): BoutSummaryStats {
	return {
		punchesThrown: +stats.TotalStrikesThrown,
		punchesLanded: +stats.TotalStrikesLanded,
		punchesMissed: +stats.TotalMissStrikes,
		landedPercentage: +stats.LandedStrikePercentage,
		missedPercentage: +stats.MissStrikePercentage,
		knockdowns: +stats.TotalKnockdowns,
		knockdownsPercentage: +stats.KnockdownPercentage
	};
}

function extractTotalInDepthStats(totalStats: TotalsStats): BoutInDepthStats {
	return {
		punchesThrown: {
			total: {
				head: +totalStats.TotalHeadStrikesThrown,
				body: +totalStats.TotalBodyStrikesThrown,
				total: +totalStats.TotalStrikesThrown
			},
			distance: {
				head: +totalStats.TotalDistanceHeadStrikesThrown,
				body: +totalStats.TotalDistanceBodyStrikesThrown,
				total: +totalStats.TotalDistanceStrikesThrown
			},
			clinch: {
				head: +totalStats.TotalClinchHeadStrikesThrown,
				body: +totalStats.TotalClinchBodyStrikesThrown,
				total: +totalStats.TotalClinchStrikesThrown
			}
		},
		punchesLanded: {
			total: {
				head: +totalStats.TotalHeadStrikesLanded,
				body: +totalStats.TotalBodyStrikesLanded,
				total: +totalStats.TotalStrikesLanded
			},
			distance: {
				head: +totalStats.TotalDistanceHeadStrikesLanded,
				body: +totalStats.TotalDistanceBodyStrikesLanded,
				total: +totalStats.TotalDistanceStrikesLanded
			},
			clinch: {
				head: +totalStats.TotalClinchHeadStrikesLanded,
				body: +totalStats.TotalClinchBodyStrikesLanded,
				total: +totalStats.TotalClinchStrikesLanded
			}
		},
		punchesMissed: {
			total: {
				head: +totalStats.TotalHeadMissStrikes,
				body: +totalStats.TotalBodyMissStrikes,
				total: +totalStats.TotalMissStrikes
			},
			distance: {
				head: +totalStats.TotalDistanceHeadMissStrikes,
				body: +totalStats.TotalDistanceBodyMissStrikes,
				total: +totalStats.TotalDistanceMissStrikes
			},
			clinch: {
				head: +totalStats.TotalClinchHeadMissStrikes,
				body: +totalStats.TotalClinchBodyMissStrikes,
				total: +totalStats.TotalClinchMissStrikes
			}
		}
	};
}

function extractRoundInDepthStats(totalStats: StrikesStats): BoutInDepthStats {
	return {
		punchesThrown: {
			total: {
				head: +totalStats.TotalHeadStrikesThrown,
				body: +totalStats.TotalBodyStrikesThrown,
				total: +totalStats.TotalStrikesThrown
			},
			distance: {
				head: +totalStats.TotalDistanceHeadStrikesThrown,
				body: +totalStats.TotalDistanceBodyStrikesThrown,
				total: +totalStats.TotalDistanceStrikesThrown
			},
			clinch: {
				head: +totalStats.TotalClinchHeadStrikesThrown,
				body: +totalStats.TotalClinchBodyStrikesThrown,
				total: +totalStats.TotalClinchStrikesThrown
			}
		},
		punchesLanded: {
			total: {
				head: +totalStats.TotalHeadStrikesLanded,
				body: +totalStats.TotalBodyStrikesLanded,
				total: +totalStats.TotalStrikesLanded
			},
			distance: {
				head: +totalStats.DistanceHeadLandedStrikes,
				body: +totalStats.DistanceBodyLandedStrikes,
				total: +totalStats.TotalDistanceStrikesLanded
			},
			clinch: {
				head: +totalStats.ClinchHeadLandedStrikes,
				body: +totalStats.ClinchBodyLandedStrikes,
				total: +totalStats.TotalClinchStrikesLanded
			}
		},
		punchesMissed: {
			total: {
				head: +totalStats.TotalHeadMissStrikes,
				body: +totalStats.TotalBodyMissStrikes,
				total: +totalStats.TotalMissStrikes
			},
			distance: {
				head: +totalStats.DistanceHeadMissStrikes,
				body: +totalStats.DistanceBodyMissStrikes,
				total: +totalStats.TotalDistanceMissStrikes
			},
			clinch: {
				head: +totalStats.ClinchHeadMissStrikes,
				body: +totalStats.ClinchBodyMissStrikes,
				total: +totalStats.TotalClinchMissStrikes
			}
		}
	};
}

function extractRoundKnockdownsStats(stats: StrikesStats): AggregatedPunchStats {
	return {
		distance: {
			head: +stats.DistanceHeadKnockdowns,
			body: +stats.DistanceBodyKnockdowns,
			total: +stats.TotalDistanceKnockdowns
		},
		clinch: {
			head: +stats.ClinchHeadKnockdowns,
			body: +stats.ClinchBodyKnockdowns,
			total: +stats.TotalClinchKnockdowns
		},
		total: {
			head: +stats.TotalHeadKnockdowns,
			body: +stats.TotalBodyKnockdowns,
			total: +stats.TotalKnockdowns
		}
	};
}

function extractTotalKnockdownsStats(totalStats: TotalsStats): AggregatedPunchStats {
	return {
		distance: {
			head: +totalStats.TotalDistanceHeadKnockdowns,
			body: +totalStats.TotalDistanceBodyKnockdowns,
			total: +totalStats.TotalDistanceKnockdowns
		},
		clinch: {
			head: +totalStats.TotalClinchHeadKnockdowns,
			body: +totalStats.TotalClinchBodyKnockdowns,
			total: +totalStats.TotalClinchKnockdowns
		},
		total: {
			head: +totalStats.TotalHeadKnockdowns,
			body: +totalStats.TotalBodyKnockdowns,
			total: +totalStats.TotalKnockdowns
		}
	};
}

const fetchRoundStats = (roundStats: StrikesStats) => {
	return {
		summary: extractSummaryStats(roundStats),
		inDepth: extractRoundInDepthStats(roundStats),
		knockdowns: extractRoundKnockdownsStats(roundStats)
	};
};

function mapRounds(rounds: Round[]) {
	return rounds.map((round, index) => {
		return {
			roundNumber: index + 1,
			blueCorner: fetchRoundStats(round.StrikesBlue),
			redCorner: fetchRoundStats(round.StrikesRed)
		};
	});
}

function mapTotalStats(stats: { TotalsRed: TotalsStats; TotalsBlue: TotalsStats }) {
	const mapCornerTotalStats = (cornerStats: TotalsStats) => {
		return {
			summary: extractSummaryStats(cornerStats),
			inDepth: extractTotalInDepthStats(cornerStats),
			knockdowns: extractTotalKnockdownsStats(cornerStats)
		};
	};

	return {
		blueCorner: mapCornerTotalStats(stats.TotalsBlue),
		redCorner: mapCornerTotalStats(stats.TotalsRed)
	};
}

export function mapBoutStats(boutStats: BoutStatsResponse): BoutStats {
	return {
		rounds: mapRounds(boutStats.Bout.Actions.Rounds),
		totals: mapTotalStats(boutStats.Bout.Actions.Totals)
	};
}

export function mapEvent(eventResponse: DetailedEventResponse): EventWithBouts {
	const event: EventInfo = {
		name: eventResponse.name,
		date: new Date(eventResponse.starts_at),
		venue: eventResponse.venue,
		organization: eventResponse.organization?.name || '',
		promotion: eventResponse.promotion.name,
		sport: eventResponse.sport_name as Sport,
		city: eventResponse.city,
		state: eventResponse.state,
		isExhibition: false, // TODO
		country: '' // TODO:
	};

	const bouts = eventResponse.fights.map(mapBout);

	return { event, bouts };
}
